/**
 * FILE-OVERVIEW:
 * - 役割: 外部アプリが出稿バケットへ自動投入した素材（熊情報など）を、ジャンルごとに一覧化する。
 * - 主な連携: components/Other/*, services/s3Service.ts（OUTPUT_BUCKET_NAME）
 * - 本番運用上の注意: 読み取り専用。外部アプリの素材は article/file/ の実体XMLだけで
 *   メモ登録（article/memo/）を伴わないため、一覧XML（cts2app）にはテーマとして載らない。
 *   そのため出稿バケットを直接読む。
 *   CTSは開発用バケットを読まず外部アプリも本番バケットにしか書かないので、
 *   開発環境（*-dev）では一覧が空になる。
 */

import { fetchAuthSession } from '@aws-amplify/auth';
import { S3Client, GetObjectCommand, ListObjectsV2Command } from '@aws-sdk/client-s3';
import type { _Object } from '@aws-sdk/client-s3';
import { OUTPUT_BUCKET_NAME, S3_REGION } from './s3Service';

/** 外部アプリが投入した素材1件 */
export interface ExternalFeedItem {
  key: string;
  /** 取り込み日時（NewsEnvelope/DateAndTime。無ければS3の更新日時） */
  receivedAt: Date;
  headline: string;
  /** NewsLineText。情報元のメール件名などが入る */
  sourceNote: string;
  body: string;
  /** MemoID（NOTET-…） */
  memoId: string;
  childMemoId: string;
  /** RevisionId。2が初版、3以上は訂正 */
  revision: number;
  creatorId: string;
  /** 見出しに【テスト】がある、または掲載不可と明記されている */
  isTest: boolean;
}

/** その他タブに並べるジャンルの定義。ジャンルを足すときはここに追加する */
export interface ExternalFeedGenre {
  key: string;
  label: string;
  matches: (item: ExternalFeedItem) => boolean;
}

/**
 * 外部アプリが自動投入した素材か。
 * CreatorID=system はアプリ自身もユーザー不明時の既定値として使う（2026-10-28時点で出稿XMLの約4割）ため、
 * 単独では判別できない。NewsLineText の「自動生成」と組み合わせて見る。
 */
export const isExternalFeedItem = (item: ExternalFeedItem): boolean =>
  item.creatorId === 'system' && item.sourceNote.startsWith('自動生成');

export const EXTERNAL_FEED_GENRES: ExternalFeedGenre[] = [
  {
    key: 'bear',
    label: '熊情報',
    // 外部アプリがファイル名と MemoID・ChildMemoID に kuma を入れる（2026-10-29から）。
    // 送信によって片方にしか無いことがあったため、どちらかにあれば対象とする。フォルダ名は見ない
    matches: (item) => /kuma/i.test(`${item.key.split('/').pop() || ''} ${item.memoId} ${item.childMemoId}`),
  },
];

/** 一覧に出す期間（日）。古い素材まで毎回読まないよう、S3の更新日時で先に絞る */
export const EXTERNAL_FEED_RECENT_DAYS = 30;

// CTSの記事本文で段落末に入る記号（NEC原本サンプル 01_記事実体.xml と同じ）
const CTS_PARAGRAPH_MARK = /띱/g;

/**
 * NewsML の日時（YYYYMMDDThhmmss+0900）を Date にする。解釈できなければ null。
 */
export const parseNewsMLDateTime = (value: string): Date | null => {
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})([+-]\d{2})(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, s, tzh, tzm] = m;
  const date = new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}${tzh}:${tzm}`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * 実体NewsMLの文字列から一覧表示用の項目を取り出す。NewsMLでなければ null。
 */
export const parseExternalFeedXml = (xmlString: string, key: string, lastModified: Date): ExternalFeedItem | null => {
  const xmlDoc = new DOMParser().parseFromString(xmlString, 'text/xml');
  if (xmlDoc.querySelector('parsererror') || !xmlDoc.querySelector('NewsItem')) return null;

  const text = (selector: string) => xmlDoc.querySelector(selector)?.textContent?.trim() || '';
  const prop = (name: string) => xmlDoc.querySelector(`Property[FormalName="${name}"]`)?.getAttribute('Value') || '';

  const headline = text('HeadLine');
  const sourceNote = text('NewsLineText');
  const revision = Number(text('RevisionId')) || 0;

  return {
    key,
    receivedAt: parseNewsMLDateTime(text('NewsEnvelope > DateAndTime')) || lastModified,
    headline,
    sourceNote,
    body: (xmlDoc.querySelector('DataContent')?.textContent || '')
      .replace(CTS_PARAGRAPH_MARK, '\n')
      // trim() は段落頭の全角スペースまで消すので、末尾の空白だけ落とす
      .replace(/\s+$/, ''),
    memoId: prop('MemoID'),
    childMemoId: prop('ChildMemoID'),
    revision,
    creatorId: prop('CreatorID'),
    isTest: headline.includes('【テスト】') || sourceNote.includes('掲載不可'),
  };
};

/**
 * 同じ素材（ChildMemoID）の訂正は最新版だけ残し、取り込み日時の新しい順に並べる。
 */
export const latestRevisionsNewestFirst = (items: ExternalFeedItem[]): ExternalFeedItem[] => {
  const byId = new Map<string, ExternalFeedItem>();
  items.forEach((item) => {
    const id = item.childMemoId || item.key;
    const prev = byId.get(id);
    if (!prev || item.revision > prev.revision
      || (item.revision === prev.revision && item.receivedAt > prev.receivedAt)) {
      byId.set(id, item);
    }
  });
  return Array.from(byId.values())
    .sort((a, b) => b.receivedAt.getTime() - a.receivedAt.getTime());
};

/**
 * 出稿バケットの article/file/ から直近の外部投入素材を読み、ジャンルごとに振り分ける。
 *
 * @returns {Promise<Record<string, ExternalFeedItem[]>>} { ジャンルkey: 取り込み日時の新しい順 }
 */
export const listExternalFeedItems = async (now = new Date()): Promise<Record<string, ExternalFeedItem[]>> => {
  const session = await fetchAuthSession();
  const s3Client = new S3Client({
    region: S3_REGION,
    credentials: session.credentials,
    requestHandler: { cache: 'no-store' },
  });

  const since = now.getTime() - EXTERNAL_FEED_RECENT_DAYS * 24 * 60 * 60 * 1000;
  const objects: _Object[] = [];
  let continuationToken: string | undefined;
  do {
    const result = await s3Client.send(new ListObjectsV2Command({
      Bucket: OUTPUT_BUCKET_NAME,
      Prefix: 'article/file/',
      ContinuationToken: continuationToken,
    }));
    (result.Contents || []).forEach((obj) => {
      if (!obj.Key?.toLowerCase().endsWith('.xml')) return;
      if (!obj.LastModified || obj.LastModified.getTime() < since) return;
      objects.push(obj);
    });
    continuationToken = result.IsTruncated ? result.NextContinuationToken : undefined;
  } while (continuationToken);

  const parsed = await Promise.all(objects.map(async (obj) => {
    try {
      const getResult = await s3Client.send(new GetObjectCommand({ Bucket: OUTPUT_BUCKET_NAME, Key: obj.Key }));
      const rawBytes = new Uint8Array(await getResult.Body!.transformToByteArray());
      const xmlString = (rawBytes.length >= 2 && rawBytes[0] === 0xFE && rawBytes[1] === 0xFF)
        ? new TextDecoder('utf-16be').decode(rawBytes)
        : new TextDecoder('utf-8').decode(rawBytes);
      return parseExternalFeedXml(xmlString, obj.Key!, obj.LastModified!);
    } catch (e) {
      console.warn('外部投入素材の解析に失敗:', obj.Key, e);
      return null;
    }
  }));
  const items = parsed.filter((item): item is ExternalFeedItem => item !== null);

  return Object.fromEntries(EXTERNAL_FEED_GENRES.map((genre) => [
    genre.key,
    latestRevisionsNewestFirst(items.filter((item) => isExternalFeedItem(item) && genre.matches(item))),
  ]));
};
